import React, { useState } from "react";
import styled, { css, keyframes } from "styled-components";

import { useDispatch } from "services/redux/rootReducer";
import restoreBackup from "../actions/backup.restoreBackup";
import { Backup } from "../types/backup";
import { AiOutlineLoading } from "react-icons/ai";
import { Icon } from "@iconify/react";

interface Props {
    backupInfo: Backup;
}

const BackupEntry: React.FC<Props> = ({ backupInfo }) => {
    const dispatch = useDispatch();
    const [isRestoring, setRestoring] = useState<boolean>(false);

    const backupTime = new Date(
        backupInfo.creationTimestamp || 0
    ).toLocaleString();

    const handleRestoreDispatch = async () => {
        if (
            window.confirm(
                `Are you sure you want to restore this backup from ${backupTime}?`
            )
        ) {
            setRestoring(true);
            await dispatch(
                restoreBackup(
                    backupInfo.source?.collections || [],
                    backupInfo.source?.outputUriPrefix
                )
            );
            setRestoring(false);
        }
    };

    return (
        <Container key={backupInfo.creationTimestamp}>
            <InfoContainer>
                <DateContainer>{backupTime}</DateContainer>
                <TriggerContainer>
                    <span>{backupInfo.trigger}</span>
                </TriggerContainer>
                <ScopeContainer>
                    {backupInfo.source?.collections
                        ? `${backupInfo.source?.collections.join(", ")}`
                        : "Entire database"}
                </ScopeContainer>
            </InfoContainer>
            <IconContainer
                rotating={isRestoring}
                onClick={handleRestoreDispatch}
            >
                {isRestoring ? (
                    <AiOutlineLoading />
                ) : (
                    <Icon icon="pepicons:rewind-time" />
                )}
            </IconContainer>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    border: solid 0.5px ${(props) => props.theme.colors.primary[50]};
    background: ${(props) => props.theme.colors.neutral[50]};
    border-radius: 0.2rem;

    padding: 1rem;

    user-select: none;

    height: 8rem;
`;

const InfoContainer = styled.div`
    display: flex;
    flex-flow: column;

    gap: 0.5rem;

    font-weight: ${(props) => props.theme.fontWeight.bold};
`;

const DateContainer = styled.div``;

const TriggerContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    width: fit-content;
    padding: 0 0.4rem;

    background: ${(props) => props.theme.colors.primary[100]};
    color: white;
    border-radius: 0.2rem;

    font-size: 0.9rem;
`;

const ScopeContainer = styled.div`
    text-transform: capitalize;
`;

const RotateAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const IconContainer = styled.div<{ rotating: boolean }>`
    font-size: 3rem;

    display: flex;
    justify-content: center;
    align-items: center;

    width: 5rem;
    height: 5rem;

    background: white;
    border-radius: 50%;

    transition: all linear 0.025s;

    color: ${(props) => props.theme.colors.primary[100]};

    &:hover {
        cursor: pointer;
        background: ${(props) =>
            props.rotating ? "white" : props.theme.colors.primary[100]};
        color: ${(props) =>
            props.rotating ? props.theme.colors.primary[100] : "white"};
    }

    & > * {
        animation: ${(props) =>
            props.rotating
                ? css`
                      ${RotateAnimation} 2s linear infinite;
                  `
                : ""};
    }
`;

export default BackupEntry;
